/**
 * Maps a patient's `actionRequired` label to the route its action button
 * should navigate to when `PatientActionsContext.handleAction` doesn't
 * recognize the action — i.e. it's a navigation-type action (routed to a
 * dedicated page) rather than a modal-type one (Start monitoring, Schedule
 * appointment, Upload records, ...), which `handleAction` already handles
 * by opening the relevant modal instead.
 *
 * Mirrors `ActionRequiredCell`'s dispatch so every surface
 * that renders the action-required button (the patients table row, the
 * Overview tab's `StatusActionCard`) resolves to the same destination.
 * Returns `null` when the action isn't a navigation-type one.
 */
export function getActionRequiredRoute(actionRequiredText: string, id: string): string | null {
    switch (actionRequiredText) {
        case 'Submit case':
            return `/patient/creation/${id}/patient-details`;
        case 'Approve case':
            return `/patient/${id}/approve`;
        case 'Deliver aligners':
            return `/patient/${id}/deliver`;
        case 'Deliver refinement aligners':
            return `/patient/${id}/deliver-refinement`;
        case 'Deliver retainers':
            return `/patient/${id}/deliver-retainers`;
        case 'Review for refinement':
            return `/patient/${id}/review-refinement`;
        case 'Review for retention':
            return `/patient/${id}/review-retention`;
        case 'Review design':
            return `/patient/${id}/approve-refinement`;
        default:
            return null;
    }
}

/**
 * Route for clicking a patient's NAME (as opposed to their action button).
 * Always goes to the overview page — performing the required action is
 * exclusively the action button's job. The one exception is patients who
 * haven't been submitted yet ('Submit case'): they have no
 * case/treatment/monitoring data yet, so the overview page's tabs would
 * render essentially empty and gated. This mirrors `PatientRedirect`'s smart
 * redirect, which already treats 'Submit case' as belonging in the creation
 * wizard rather than the overview page.
 *
 * Shared by the patients table (`PatientCell`) and the conversations /
 * messages surfaces (`ConversationPanel`) so name-click behavior stays
 * consistent everywhere a patient name is rendered.
 */
export function getNameClickRoute(actionRequiredText: string | null, id: string): string {
    if (actionRequiredText === 'Submit case') {
        return `/patient/creation/${id}/patient-details`;
    }
    return `/patient/${id}/overview`;
}
